import React, { useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp, Eye, Edit2, X, Check, Users } from "lucide-react";
import {
  useGetFormsQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useGetFormSubmissionsQuery,
} from "../api/businessApi";

const FIELD_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "textarea", label: "Long Text" },
  { value: "radio", label: "Single Choice (Radio)" },
  { value: "checkbox", label: "Multiple Choice (Checkbox)" },
  { value: "select", label: "Dropdown" },
];

function generateId() {
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Field Editor Row ─────────────────────────────────────────────────────────
function FieldRow({ field, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const needsOptions = ["radio", "checkbox", "select"].includes(field.type);
  const [optionInput, setOptionInput] = useState("");

  const addOption = () => {
    if (!optionInput.trim()) return;
    onChange({ ...field, options: [...(field.options || []), optionInput.trim()] });
    setOptionInput("");
  };

  const removeOption = (i) => {
    onChange({ ...field, options: field.options.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start gap-3">
        {/* Move up/down */}
        <div className="flex flex-col gap-1 pt-1">
          <button onClick={onMoveUp} disabled={isFirst} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
            <ChevronUp size={16} />
          </button>
          <button onClick={onMoveDown} disabled={isLast} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
            <ChevronDown size={16} />
          </button>
        </div>

        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
              <input
                type="text"
                value={field.label}
                onChange={(e) => onChange({ ...field, label: e.target.value })}
                placeholder="e.g. Full Name"
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#135879]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={field.type}
                onChange={(e) => onChange({ ...field, type: e.target.value, options: [] })}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#135879]"
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder (optional)</label>
              <input
                type="text"
                value={field.placeholder || ""}
                onChange={(e) => onChange({ ...field, placeholder: e.target.value })}
                placeholder="Hint text shown in field"
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#135879]"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={field.required || false}
                  onChange={(e) => onChange({ ...field, required: e.target.checked })}
                  className="rounded"
                />
                Required
              </label>
            </div>
          </div>

          {needsOptions && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Options</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOption())}
                  placeholder="Type option and press Enter"
                  className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#135879]"
                />
                <button
                  onClick={addOption}
                  className="px-3 py-1.5 bg-[#135879] text-white rounded text-sm hover:bg-[#0e4562]"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(field.options || []).map((opt, i) => (
                  <span key={i} className="flex items-center gap-1 bg-white border border-gray-300 rounded-full px-3 py-1 text-sm">
                    {opt}
                    <button onClick={() => removeOption(i)} className="text-gray-400 hover:text-red-500 ml-1">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <button onClick={onRemove} className="text-gray-400 hover:text-red-500 pt-1">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Form Editor Modal ────────────────────────────────────────────────────────
function FormEditor({ form, onSave, onClose }) {
  const [title, setTitle] = useState(form?.title || "");
  const [description, setDescription] = useState(form?.description || "");
  const [fields, setFields] = useState(form?.fields || []);

  const addField = (type = "text") => {
    setFields([...fields, { id: generateId(), type, label: "", required: false, options: [], placeholder: "" }]);
  };

  const updateField = (index, updated) => {
    setFields(fields.map((f, i) => (i === index ? updated : f)));
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const moveField = (index, dir) => {
    const newFields = [...fields];
    const target = index + dir;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setFields(newFields);
  };

  const handleSave = () => {
    if (!title.trim()) return alert("Form title is required");
    onSave({ title, description, fields });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#135879]">{form ? "Edit Form" : "New Form"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Form Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Expression of Interest: Supply Bidding"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#135879]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (shown to respondents)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Brief explanation of the form's purpose..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#135879] resize-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Fields ({fields.length})</label>
              <div className="flex gap-2">
                {FIELD_TYPES.slice(0, 5).map((t) => (
                  <button
                    key={t.value}
                    onClick={() => addField(t.value)}
                    className="text-xs px-2.5 py-1 border border-gray-300 rounded-full hover:bg-gray-50 text-gray-600"
                  >
                    + {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mb-3">
              {FIELD_TYPES.slice(5).map((t) => (
                <button
                  key={t.value}
                  onClick={() => addField(t.value)}
                  className="text-xs px-2.5 py-1 border border-gray-300 rounded-full hover:bg-gray-50 text-gray-600"
                >
                  + {t.label}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  index={index}
                  onChange={(updated) => updateField(index, updated)}
                  onRemove={() => removeField(index)}
                  onMoveUp={() => moveField(index, -1)}
                  onMoveDown={() => moveField(index, 1)}
                  isFirst={index === 0}
                  isLast={index === fields.length - 1}
                />
              ))}
              {fields.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
                  No fields yet — use the buttons above to add fields
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 bg-[#135879] text-white rounded-lg text-sm font-medium hover:bg-[#0e4562] flex items-center gap-2"
          >
            <Check size={16} /> Save Form
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Submissions Viewer ───────────────────────────────────────────────────────
function SubmissionsDrawer({ form, onClose }) {
  const { data: submissions = [], isLoading } = useGetFormSubmissionsQuery(form.id);

  const exportCsv = () => {
    if (!submissions.length) return;
    const fields = form.fields || [];
    const headers = ["Submitted At", "Name", "Email", ...fields.map((f) => f.label)];
    const rows = submissions.map((s) => [
      new Date(s.submitted_at).toLocaleString(),
      s.submitter_name || "",
      s.submitter_email || "",
      ...fields.map((f) => {
        const val = s.data[f.id];
        return Array.isArray(val) ? val.join("; ") : (val || "");
      }),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${form.title.replace(/\s+/g, "_")}_submissions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-[#135879]">Submissions</h2>
            <p className="text-sm text-gray-500 mt-1">{form.title}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
            >
              Export CSV
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">Loading submissions...</div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users size={32} className="mx-auto mb-3 opacity-50" />
              No submissions yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Submitted</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                    {(form.fields || []).map((f) => (
                      <th key={f.id} className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                        {f.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(s.submitted_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">{s.submitter_name || "—"}</td>
                      <td className="px-4 py-3">{s.submitter_email || "—"}</td>
                      {(form.fields || []).map((f) => {
                        const val = s.data[f.id];
                        return (
                          <td key={f.id} className="px-4 py-3 max-w-[200px]">
                            {Array.isArray(val) ? val.join(", ") : (val || "—")}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main AdminForms Page ─────────────────────────────────────────────────────
const AdminForms = () => {
  const { data: forms = [], isLoading } = useGetFormsQuery();
  const [createForm] = useCreateFormMutation();
  const [updateForm] = useUpdateFormMutation();
  const [deleteForm] = useDeleteFormMutation();

  const [editingForm, setEditingForm] = useState(null); // null | "new" | form object
  const [viewingSubmissions, setViewingSubmissions] = useState(null);

  const handleSave = async (data) => {
    if (editingForm === "new") {
      await createForm(data);
    } else {
      await updateForm({ id: editingForm.id, ...data });
    }
    setEditingForm(null);
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Delete form "${form.title}"? This will also delete all submissions.`)) return;
    await deleteForm(form.id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#135879]">Forms</h1>
            <p className="text-gray-500 text-sm mt-1">Create forms to attach to announcements and resources</p>
          </div>
          <button
            onClick={() => setEditingForm("new")}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#135879] text-white rounded-lg font-medium hover:bg-[#0e4562]"
          >
            <Plus size={18} /> New Form
          </button>
        </div>

        {/* Forms List */}
        {isLoading ? (
          <div className="text-center py-16 text-gray-500">Loading forms...</div>
        ) : forms.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
            <div className="text-gray-400 text-lg mb-2">No forms yet</div>
            <p className="text-gray-400 text-sm mb-4">Create your first form to attach to an announcement</p>
            <button
              onClick={() => setEditingForm("new")}
              className="px-5 py-2.5 bg-[#135879] text-white rounded-lg font-medium hover:bg-[#0e4562]"
            >
              Create Form
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {forms.map((form) => (
              <div key={form.id} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">{form.title}</h3>
                  {form.description && (
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{form.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-gray-400">{(form.fields || []).length} fields</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${form.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {form.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewingSubmissions(form)}
                    className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 text-gray-600"
                  >
                    <Users size={14} /> Responses
                  </button>
                  <button
                    onClick={() => setEditingForm(form)}
                    className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 text-gray-600"
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(form)}
                    className="flex items-center gap-1.5 px-3 py-2 border border-red-200 rounded-lg text-sm hover:bg-red-50 text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {editingForm && (
        <FormEditor
          form={editingForm === "new" ? null : editingForm}
          onSave={handleSave}
          onClose={() => setEditingForm(null)}
        />
      )}
      {viewingSubmissions && (
        <SubmissionsDrawer
          form={viewingSubmissions}
          onClose={() => setViewingSubmissions(null)}
        />
      )}
    </div>
  );
};

export default AdminForms;
